import React, { useState } from 'react';
import { RefreshCw, Search, Download, Eye, Printer, FileText, CheckCircle, ArrowDownLeft, Calendar, X, RotateCcw, Filter, Truck } from 'lucide-react';
import CustomDatePicker from '../../shared/CustomDatePicker';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';

const getTodayIso = () => new Date().toISOString().split('T')[0];

const getIsoDateStr = (dateVal) => {
    if (!dateVal) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
    try {
        const parsed = new Date(dateVal);
        if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch (e) {}
    return '';
};

const TransferHistoryView = ({ transfers = [], onViewTransfer, onPrintPdf }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [dateFilterMode, setDateFilterMode] = useState('ALL'); // Default to ALL dates
    const [customDate, setCustomDate] = useState(getTodayIso());

    const effectiveDateFilter = dateFilterMode === 'TODAY' ? getTodayIso() : (dateFilterMode === 'CUSTOM' ? customDate : '');

    // Filter transfers by active date range first so summary cards match selected date range
    const dateFilteredTransfers = transfers.filter(t => {
        if (!effectiveDateFilter) return true;
        const tDateIso = getIsoDateStr(t.date || t.timestamp);
        return tDateIso === effectiveDateFilter;
    });

    const filteredTransfers = dateFilteredTransfers.filter(t => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            (t.transferNo || '').toLowerCase().includes(term) ||
            (t.requestNo || '').toLowerCase().includes(term) ||
            (t.salesperson && t.salesperson.toLowerCase().includes(term)) ||
            (t.salespersonName && t.salespersonName.toLowerCase().includes(term)) ||
            (t.vehicle && t.vehicle.toLowerCase().includes(term)) ||
            (t.items || []).some(i => (i.productName || i.name || i.product || '').toLowerCase().includes(term));
        
        const matchesType = typeFilter === 'ALL' || (t.type || '').toUpperCase() === typeFilter.toUpperCase();

        return matchesSearch && matchesType;
    });

    const isFiltered = searchTerm || typeFilter !== 'ALL' || dateFilterMode !== 'ALL';

    const handleResetFilters = () => {
        setSearchTerm('');
        setTypeFilter('ALL');
        setDateFilterMode('ALL');
    };

    const getTypeBadge = (type) => {
        if (type?.toUpperCase() === 'LOADING') {
            return <span className="px-2.5 py-1 text-xs rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-bold border border-blue-200 dark:border-blue-700">Loading</span>;
        }
        return <span className="px-2.5 py-1 text-xs rounded-full bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 font-bold border border-purple-200 dark:border-purple-700">Offloading</span>;
    };

    return (
        <div className="space-y-5">
            {/* Top Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-extrabold text-slate-800 dark:text-white flex items-center gap-2 tracking-tight">
                        <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                            <RefreshCw className="w-5 h-5" />
                        </div>
                        Transfer History
                    </h1>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">
                        Permanent audit trail of all completed stock transfers and offload returns
                    </p>
                </div>
                <button
                    onClick={() => alert('Exporting Transfer History CSV...')}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-white rounded-xl font-bold text-xs transition-colors border border-slate-200 dark:border-slate-600 shadow-xs cursor-pointer"
                >
                    <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Export CSV
                </button>
            </div>

            {/* Stat Cards - Interactive 2 Rows x 2 Columns with Distinct Color Outlines */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                <div
                    onClick={() => setTypeFilter('ALL')}
                    className={`bg-white dark:bg-slate-800 p-2.5 sm:p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        typeFilter === 'ALL'
                            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                            : 'border-slate-200 dark:border-slate-700/80 hover:border-blue-300 shadow-2xs'
                    } flex items-center gap-2 sm:gap-3`}
                >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base sm:text-xl font-black text-slate-800 dark:text-white leading-tight truncate">{dateFilteredTransfers.length}</div>
                        <div className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">Total Transfers</div>
                    </div>
                </div>

                <div
                    onClick={() => setTypeFilter('LOADING')}
                    className={`bg-white dark:bg-slate-800 p-2.5 sm:p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        typeFilter === 'LOADING'
                            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                            : 'border-slate-200 dark:border-slate-700/80 hover:border-blue-300 shadow-2xs'
                    } flex items-center gap-2 sm:gap-3`}
                >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base sm:text-xl font-black text-slate-800 dark:text-white leading-tight truncate">
                            {dateFilteredTransfers.filter(t => (t.type || '').toLowerCase() === 'loading').length}
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">Loading Transfers</div>
                    </div>
                </div>

                <div
                    onClick={() => setTypeFilter('OFFLOAD')}
                    className={`bg-white dark:bg-slate-800 p-2.5 sm:p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        typeFilter === 'OFFLOAD'
                            ? 'border-purple-500 ring-2 ring-purple-500/20 shadow-md'
                            : 'border-slate-200 dark:border-slate-700/80 hover:border-purple-300 shadow-2xs'
                    } flex items-center gap-2 sm:gap-3`}
                >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
                        <ArrowDownLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base sm:text-xl font-black text-slate-800 dark:text-white leading-tight truncate">
                            {dateFilteredTransfers.filter(t => (t.type || '').toLowerCase() === 'offload').length}
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">Offload Returns</div>
                    </div>
                </div>

                <div
                    onClick={() => setTypeFilter('COMPLETED')}
                    className={`bg-white dark:bg-slate-800 p-2.5 sm:p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        typeFilter === 'COMPLETED'
                            ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                            : 'border-slate-200 dark:border-slate-700/80 hover:border-emerald-300 shadow-2xs'
                    } flex items-center gap-2 sm:gap-3`}
                >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base sm:text-xl font-black text-slate-800 dark:text-white leading-tight truncate">
                            {dateFilteredTransfers.filter(t => (t.status || '').toLowerCase() === 'completed').length}
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">Completed Logs</div>
                    </div>
                </div>
            </div>

            {/* Filter Toolbar */}
            <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-sm space-y-3">
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search transfer no, request no, vehicle..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-9 h-11 text-xs bg-slate-50 dark:bg-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white font-medium placeholder-slate-400 transition-all"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Type Filter */}
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-3.5 h-11 text-xs bg-slate-50 dark:bg-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white font-semibold transition-all cursor-pointer"
                    >
                        <option value="ALL">All Types</option>
                        <option value="LOADING">Loading</option>
                        <option value="OFFLOAD">Offloading</option>
                    </select>

                    {/* Quick Date Filter Pills */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-700/80 p-1 rounded-xl gap-1 overflow-x-auto no-scrollbar">
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('TODAY')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                                dateFilterMode === 'TODAY'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            <Calendar className="w-3.5 h-3.5" /> Today
                        </button>
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('ALL')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                dateFilterMode === 'ALL'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            All Dates
                        </button>
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('CUSTOM')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                dateFilterMode === 'CUSTOM'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            Custom
                        </button>
                    </div>

                    {/* Custom Date Input */}
                    {dateFilterMode === 'CUSTOM' && (
                        <CustomDatePicker
                            mode="single"
                            value={customDate}
                            onChange={(val) => setCustomDate(val)}
                            quickFilters={false}
                        />
                    )}
                </div>

                {/* Active Filter Bar */}
                {isFiltered && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700 text-xs">
                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
                            <Filter className="w-3.5 h-3.5 text-blue-500" />
                            <span>Showing results for:</span>
                            <span className="font-bold text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md">
                                {dateFilterMode === 'TODAY' ? `Today (${getTodayIso()})` : dateFilterMode === 'CUSTOM' ? `Date: ${customDate}` : 'All Dates'}
                            </span>
                            {typeFilter !== 'ALL' && (
                                <span className="font-bold text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md">
                                    Type: {typeFilter}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={handleResetFilters}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                        >
                            <RotateCcw className="w-3 h-3" /> Clear Filters
                        </button>
                    </div>
                )}
            </div>

            {/* Content List: Mobile Cards + Desktop Table */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-sm overflow-hidden">
                {filteredTransfers.length === 0 ? (
                    <div className="p-10 text-center space-y-3">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto">
                            <RefreshCw className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-sm text-slate-800 dark:text-white">No Transfer Records Found</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                                {dateFilterMode === 'TODAY'
                                    ? `There are no transfer history records for today (${getTodayIso()}).`
                                    : 'No transfer history records match your search or filter criteria.'}
                            </p>
                        </div>
                        {dateFilterMode === 'TODAY' && (
                            <div className="pt-2">
                                <button
                                    onClick={() => setDateFilterMode('ALL')}
                                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all cursor-pointer"
                                >
                                    View All Dates
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Mobile PWA Card View */}
                        <div className="block md:hidden p-3 space-y-3">
                            {filteredTransfers.map((t) => (
                                <div key={t.id} className="p-4 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-slate-800 shadow-xs space-y-3 transition-all">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-extrabold text-sm text-slate-800 dark:text-white">{t.transferNo}</div>
                                            <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                                                <Calendar className="w-3 h-3 text-slate-400" /> {formatWarehouseDate(t.date)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {getTypeBadge(t.type)}
                                            <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 font-bold border border-emerald-200 dark:border-emerald-700">
                                                Completed
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-700/60 text-xs">
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">From</span>
                                            <span className="font-bold text-slate-800 dark:text-slate-200">{t.fromLocation}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">To</span>
                                            <span className="font-bold text-slate-800 dark:text-slate-200">{t.toLocation}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Vehicle</span>
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-bold text-[11px] border border-blue-100 dark:border-blue-800">
                                                    <Truck className="w-3 h-3" /> {t.vehicle || 'Van 01'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Total Quantity</span>
                                            <span className="font-black text-slate-900 dark:text-white text-sm">{t.totalQty} ({t.itemsCount} items)</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-2 pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
                                        <button
                                            onClick={() => onViewTransfer(t)}
                                            className="h-10 px-3.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                                        >
                                            <Eye className="w-4 h-4 text-blue-500" /> View Details
                                        </button>
                                        <button
                                            onClick={() => onPrintPdf(t)}
                                            className="h-10 px-3.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <Printer className="w-4 h-4 text-slate-500" /> PDF
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 font-extrabold border-b border-slate-100 dark:border-slate-700 uppercase tracking-wider text-[11px]">
                                    <tr>
                                        <th className="p-4 pl-6">Transfer No.</th>
                                        <th className="p-4">Date</th>
                                        <th className="p-4">Transfer Type</th>
                                        <th className="p-4">From</th>
                                        <th className="p-4">To</th>
                                        <th className="p-4">Vehicle</th>
                                        <th className="p-4">Items</th>
                                        <th className="p-4">Total Qty</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                    {filteredTransfers.map((t) => (
                                        <tr key={t.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-700/40 transition-colors">
                                            <td className="p-4 pl-6 font-extrabold text-slate-800 dark:text-white">{t.transferNo}</td>
                                            <td className="p-4 text-slate-600 dark:text-slate-300 font-medium">{formatWarehouseDate(t.date)}</td>
                                            <td className="p-4">{getTypeBadge(t.type)}</td>
                                            <td className="p-4 text-slate-700 dark:text-slate-300 font-medium">{t.fromLocation}</td>
                                            <td className="p-4 text-slate-700 dark:text-slate-300 font-medium">{t.toLocation}</td>
                                            <td className="p-4">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-bold text-[11px] border border-blue-100 dark:border-blue-800">
                                                        <Truck className="w-3 h-3" /> {t.vehicle || 'Van 01'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-600 dark:text-slate-300 font-semibold">{t.itemsCount}</td>
                                            <td className="p-4 font-black text-slate-800 dark:text-white">{t.totalQty}</td>
                                            <td className="p-4">
                                                <span className="px-2.5 py-1 text-xs rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 font-bold border border-emerald-200 dark:border-emerald-700">
                                                    Completed
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => onViewTransfer(t)}
                                                        className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-center cursor-pointer"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => onPrintPdf(t)}
                                                        className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-center cursor-pointer"
                                                        title="Print PDF"
                                                    >
                                                        <Printer className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TransferHistoryView;

